import { useCallback, useMemo, useState } from "react";
import {
  Button,
  Flex,
  FullscreenTakeover,
  Step,
  StepIndicator,
  Text,
  toast,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { DiscardChangesModal } from "~/components/DiscardChangesModal";
import {
  useDataPackageExportLazyQuery,
  useGetFormulasInUseLazyQuery,
  useImportDataPackageMutation,
  useUpdateDataPackageMutation,
} from "~/graphql/supergraphHooks";
import { SPACING } from "~/styles/variables";
import {
  buildInvalidHierarchyTemplateError,
  useBeforeUnloadPrompt,
  useHierarchyUpload,
} from "../hooks";
import { FormulasInUseWarningModal } from "./FormulasInUseWarningModal";
import { HierarchyStep } from "./HierarchyStep";
import * as styles from "./styles_vanilla.css";
import { TemplateSettingsStep, type TemplateSettingsFormValues } from "./TemplateSettingsStep";

const STEPS = {
  TEMPLATE_SETTINGS: "template-settings",
  HIERARCHY: "hierarchy",
} as const;

type StepId = (typeof STEPS)[keyof typeof STEPS];

const STEP_CONFIG: { id: StepId; name: string }[] = [
  { id: STEPS.TEMPLATE_SETTINGS, name: "Data package settings" },
  { id: STEPS.HIERARCHY, name: "Hierarchy" },
];

type DataPackageOption = {
  id: number;
  name: string;
  arenaId?: number | null;
};

export type DataPackageTakeoverProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  arenaId: number | null;
  arenaName?: string;
  /** When provided, renders in edit mode (update hierarchy). Otherwise create mode. */
  dataPackageId?: number;
  dataPackageVersionId?: number | null;
  dataPackages?: DataPackageOption[];
};

export const DataPackageTakeover = ({
  isOpen,
  onClose,
  onSuccess,
  arenaId,
  arenaName,
  dataPackageId,
  dataPackageVersionId = null,
  dataPackages = [],
}: DataPackageTakeoverProps) => {
  const isEditMode = dataPackageId != null;

  const upload = useHierarchyUpload(arenaId);
  const [currentStep, setCurrentStep] = useState<StepId>(
    isEditMode ? STEPS.HIERARCHY : STEPS.TEMPLATE_SETTINGS,
  );
  const [templateSettings, setTemplateSettings] = useState<TemplateSettingsFormValues>({
    name: "",
    description: "",
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(undefined);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreated, setIsCreated] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);

  const [fetchDataPackageExport] = useDataPackageExportLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [importDataPackageMutation] = useImportDataPackageMutation();
  const [updateDataPackageMutation] = useUpdateDataPackageMutation();
  const [fetchFormulasInUse, { data: formulasData, loading: formulasLoading }] =
    useGetFormulasInUseLazyQuery();

  const currentStepIndex = STEP_CONFIG.findIndex((step) => step.id === currentStep);

  useBeforeUnloadPrompt(isOpen && !isCreated);

  const steps: Step[] = useMemo(
    () =>
      STEP_CONFIG.map((step, index) => {
        let status: "active" | "inactive" | "complete" = "inactive";
        if (isCreated) {
          status = "complete";
        } else if (index < currentStepIndex) {
          status = "complete";
        } else if (index === currentStepIndex) {
          status = "active";
        }
        return {
          name: step.name,
          url: step.id,
          status,
          hidden: false,
          index,
        };
      }),
    [currentStepIndex, isCreated],
  );

  const resetState = useCallback(() => {
    upload.reset();
    setTemplateSettings({ name: "", description: "" });
    setSelectedTemplateId(undefined);
    setIsCreated(false);
    setShowDiscardModal(false);
    setShowWarningModal(false);
    setCurrentStep(isEditMode ? STEPS.HIERARCHY : STEPS.TEMPLATE_SETTINGS);
  }, [upload, isEditMode]);

  const handleTemplateSettingsNext = useCallback((values: TemplateSettingsFormValues) => {
    setTemplateSettings(values);
    setCurrentStep(STEPS.HIERARCHY);
  }, []);

  const handleBack = useCallback(() => {
    setCurrentStep(STEPS.TEMPLATE_SETTINGS);
  }, []);

  const handleCancel = useCallback(() => {
    if (isEditMode || !isCreated) {
      setShowDiscardModal(true);
      return;
    }

    resetState();
    onClose();
  }, [isEditMode, isCreated, onClose, resetState]);

  const handleBackToDataPackages = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleKeepEditing = useCallback(() => {
    setShowDiscardModal(false);
  }, []);

  const handleDiscard = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleTemplateChange = useCallback((value: string | string[] | number | number[]) => {
    if (typeof value === "string") {
      setSelectedTemplateId(value);
    }
  }, []);

  const handleDownloadTemplate = useCallback(async () => {
    const targetId = isEditMode ? dataPackageId : Number(selectedTemplateId);
    if (targetId == null || Number.isNaN(targetId)) return;

    setIsDownloading(true);
    try {
      const { data } = await fetchDataPackageExport({
        variables: { arenaId, dataPackageId: targetId },
      });

      const preSignedUrl = data?.finpubDataPackageExport?.preSignedUrl;
      if (!preSignedUrl) {
        toast.error("Failed to generate download URL");
        return;
      }

      window.open(preSignedUrl, "_blank", "noopener,noreferrer");
    } catch {
      toast.error(
        isEditMode
          ? "Failed to download hierarchy. Please try again."
          : "Failed to download template. Please try again.",
      );
    } finally {
      setIsDownloading(false);
    }
  }, [isEditMode, dataPackageId, selectedTemplateId, fetchDataPackageExport, arenaId]);

  const performUpdate = useCallback(async () => {
    if (!upload.s3Key || !isEditMode) return;

    setIsSubmitting(true);
    try {
      const { data } = await updateDataPackageMutation({
        variables: { dataPackageId, s3Key: upload.s3Key, arenaId },
      });

      if (data?.finpubDataPackageUpdate?.success) {
        toast.success("Hierarchy successfully updated");
        resetState();
        onClose();
        onSuccess?.();
      } else {
        toast.error(data?.finpubDataPackageUpdate?.message ?? "Failed to update hierarchy.");
      }
    } catch {
      toast.error("Failed to update hierarchy. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    upload.s3Key,
    isEditMode,
    dataPackageId,
    arenaId,
    updateDataPackageMutation,
    resetState,
    onClose,
    onSuccess,
  ]);

  const handleSaveUpdate = useCallback(async () => {
    if (!upload.s3Key) {
      toast.error("Failed to update hierarchy. Please try again.");
      return;
    }

    if (arenaId === null || dataPackageVersionId === null) {
      await performUpdate();
      return;
    }

    setIsSubmitting(true);
    try {
      const { data } = await fetchFormulasInUse({
        variables: { arenaId, dataPackageVersionId },
      });

      const formulas = data?.finpubFormulasUsingDataPackage ?? [];
      if (formulas.length > 0) {
        setShowWarningModal(true);
        setIsSubmitting(false);
        return;
      }

      await performUpdate();
    } catch {
      toast.error("Failed to check formula usage. Please try again.");
      setIsSubmitting(false);
    }
  }, [upload.s3Key, arenaId, dataPackageVersionId, fetchFormulasInUse, performUpdate]);

  const handleConfirmUpdate = useCallback(async () => {
    if (isSubmitting) return;
    setShowWarningModal(false);
    await performUpdate();
  }, [isSubmitting, performUpdate]);

  const handleCancelWarning = useCallback(() => {
    setShowWarningModal(false);
  }, []);

  const handleCreateDataPackage = useCallback(async () => {
    if (!upload.s3Key) return;

    setIsSubmitting(true);
    try {
      const { data } = await importDataPackageMutation({
        variables: {
          arenaId,
          s3Key: upload.s3Key,
          name: templateSettings.name || undefined,
          description: templateSettings.description || undefined,
        },
      });

      if (data?.finpubDataPackageImport?.dataPackageId) {
        setIsCreated(true);
        onSuccess?.();
      } else {
        const message = data?.finpubDataPackageImport?.message;
        if (message?.toLowerCase().includes("format")) {
          toast.error(buildInvalidHierarchyTemplateError(message));
          return;
        }

        toast.error(message ?? "Failed to create data package.");
      }
    } catch {
      toast.error("Failed to create data package. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [upload.s3Key, templateSettings, importDataPackageMutation, onSuccess, arenaId]);

  if (!isOpen) {
    return null;
  }

  const takeoverTitle = isEditMode ? "Update hierarchy" : "New data package";
  const takeoverSubtitle = !isEditMode && arenaId !== null ? arenaName : undefined;

  const renderBody = () => {
    if (!isEditMode && isCreated) {
      return (
        <Flex
          flexGrow={1}
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          gap={SPACING.LARGE}
          className={styles.successScreen}
        >
          <Icon
            type="circle-check"
            variant="outline"
            size="xxx-large"
            color={Colors.Success600}
          />
          <Text variant="h3">Data package has been created</Text>
          <Text variant="secondary-md">
            You can now create formulas and queries from this hierarchy
          </Text>
          <Button variant="primary" onClick={handleBackToDataPackages}>
            Back to data packages
          </Button>
        </Flex>
      );
    }

    if (!isEditMode && currentStep === STEPS.TEMPLATE_SETTINGS) {
      return (
        <TemplateSettingsStep
          initialValues={templateSettings}
          onNext={handleTemplateSettingsNext}
          arenaId={arenaId}
        />
      );
    }

    if (isEditMode) {
      return (
        <HierarchyStep
          mode="update"
          upload={upload}
          isDownloading={isDownloading}
          isSubmitting={isSubmitting}
          onDownload={handleDownloadTemplate}
          onSave={handleSaveUpdate}
        />
      );
    }

    return (
      <HierarchyStep
        mode="create"
        upload={upload}
        dataPackages={dataPackages}
        arenaName={arenaName}
        selectedTemplateId={selectedTemplateId}
        isDownloading={isDownloading}
        isSubmitting={isSubmitting}
        onTemplateChange={handleTemplateChange}
        onDownload={handleDownloadTemplate}
        onSave={handleCreateDataPackage}
        onBack={handleBack}
      />
    );
  };

  const bodyStyle =
    (isEditMode || currentStep === STEPS.HIERARCHY) && !isCreated
      ? { padding: 0, gap: 0 }
      : undefined;

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header
        title={takeoverTitle}
        subtitle={takeoverSubtitle}
        leftActionContent={
          <Button variant="muted" icon="xmark" onClick={handleCancel}>
            Exit
          </Button>
        }
      />
      {!isEditMode && (
        <FullscreenTakeover.Subheader>
          <StepIndicator steps={steps} disableStepLinks noPadding />
        </FullscreenTakeover.Subheader>
      )}
      <FullscreenTakeover.Body style={bodyStyle}>{renderBody()}</FullscreenTakeover.Body>
      <DiscardChangesModal
        show={showDiscardModal}
        title="Discard data package?"
        onKeepEditing={handleKeepEditing}
        onDiscard={handleDiscard}
      />
      {isEditMode && (
        <FormulasInUseWarningModal
          show={showWarningModal}
          onCancel={handleCancelWarning}
          onConfirm={handleConfirmUpdate}
          isSubmitting={isSubmitting}
          formulas={formulasData?.finpubFormulasUsingDataPackage ?? null}
          isLoading={formulasLoading}
        />
      )}
    </FullscreenTakeover>
  );
};
